import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/korkort-husbil";

const FAQ = [
  {
    q: "Vilket körkort behövs för husbil?",
    a: "Beror på husbilens totalvikt. Upp till 3 500 kg — vanligt B-kort. 3 501–4 250 kg — utökat B (B96) eller BE. 4 251–7 500 kg — C1. Över 7 500 kg — C.",
  },
  {
    q: "Hur många får åka i husbilen?",
    a: "Lika många som antalet bältade sittplatser i registreringsbeviset. På B-kort begränsas passagerarantalet av bilens registrering, inte av körkortet i sig.",
  },
  {
    q: "Får jag dra släp med husbilen på B-kort?",
    a: "Ja — så länge husbilens och släpets sammanlagda totalvikt inte överstiger 3 500 kg. Vill du dra tyngre släp behöver du utökat B (B96) eller BE.",
  },
  {
    q: "Kan jag köra ner totalvikten?",
    a: "Ibland — vissa husbilar kan viktnedregistreras till 3 500 kg om chassit och konstruktionen tillåter. Ring så tittar vi på just din bil: 070-862 97 43.",
  },
];

type Result = {
  license: string;
  color: string;
  desc: string;
  ok: boolean;
};

function classify(weight: number, has96: boolean): Result {
  if (weight <= 3500) return { license: "B", color: "emerald", desc: "Vanligt B-körkort räcker.", ok: true };
  if (weight <= 4250) return {
    license: has96 ? "B96 (✓ har)" : "B96 eller BE",
    color: has96 ? "emerald" : "amber",
    desc: has96 ? "Du har rätt behörighet." : "Kräver utökat B (B96) — 4 timmars provlektion + prov.",
    ok: has96,
  };
  if (weight <= 7500) return { license: "C1", color: "red", desc: "Kräver C1-körkort (medeltung lastbil).", ok: false };
  return { license: "C", color: "red", desc: "Kräver C-körkort (tung lastbil).", ok: false };
}

export const Route = createFileRoute("/verktyg/korkort-husbil")({
  head: () => ({
    meta: [
      { title: "Vilket körkort krävs för husbil? | AK Husbil" },
      { name: "description", content: "Räkna ut vilket körkort du behöver för din husbil. Ange totalvikt — vi visar B, B96, BE, C1 eller C direkt. Guide + FAQ." },
      { name: "keywords", content: "körkort husbil, vilket körkort för husbil, husbil B-kort, husbil totalvikt, hur tung husbil på B-kort, B96 husbil" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Vilket körkort krävs för husbil?" },
      { property: "og:description", content: "Räkna ut behörighet direkt utifrån husbilens totalvikt." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const [weight, setWeight] = useState(3500);
  const [has96, setHas96] = useState(false);
  const result = useMemo(() => classify(weight, has96), [weight, has96]);
  const colorClass =
    result.color === "emerald" ? "bg-emerald-600" :
    result.color === "amber" ? "bg-amber-500" : "bg-red-600";

  return (
    <>
      <PageHeader
        label="Verktyg · Körkortskoll"
        title={<>Vilket körkort krävs för <span className="font-italic-display text-accent">husbil</span>?</>}
        sub="Skriv in husbilens totalvikt så visar vi vilket körkort som krävs. Fungerar även för husvagn+bil-ekipage."
      />

      <section className="container-edge py-10">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
            <label className="block">
              <span className="text-sm font-medium">Totalvikt (kg)</span>
              <div className="mt-1 flex items-baseline justify-between text-sm text-muted-foreground">
                <span>Står i registreringsbeviset (rad F.1 eller F.2)</span>
                <span className="text-lg font-display text-foreground">{weight.toLocaleString("sv-SE")} kg</span>
              </div>
              <input type="range" min={2000} max={8000} step={50} value={weight} onChange={(e) => setWeight(Number(e.target.value))} className="mt-2 w-full accent-primary" />
              <input type="number" min={2000} max={12000} step={50} value={weight} onChange={(e) => setWeight(Number(e.target.value))} className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </label>

            <label className="mt-6 flex items-center gap-3 text-sm">
              <input type="checkbox" checked={has96} onChange={(e) => setHas96(e.target.checked)} className="h-4 w-4 accent-primary" />
              Jag har utökat B (B96)
            </label>

            <div className="mt-6 grid grid-cols-4 gap-2 text-center text-xs">
              {[
                { l: "≤ 3500", k: "B" },
                { l: "≤ 4250", k: "B96/BE" },
                { l: "≤ 7500", k: "C1" },
                { l: "> 7500", k: "C" },
              ].map((r) => (
                <div key={r.k} className="rounded-lg border border-border bg-background p-2">
                  <p className="font-semibold">{r.k}</p>
                  <p className="text-muted-foreground">{r.l} kg</p>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-3xl p-8 text-white shadow-xl ${colorClass}`}>
            <div className="flex items-center gap-2">
              {result.ok ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
              <span className="text-sm uppercase tracking-wider opacity-90">
                {result.ok ? "Du har rätt behörighet" : "Extra behörighet krävs"}
              </span>
            </div>
            <p className="mt-4 font-display text-5xl">{result.license}</p>
            <p className="mt-3 text-white/90">{result.desc}</p>

            <div className="mt-8 space-y-3 border-t border-white/20 pt-6 text-sm text-white/90">
              <p><strong className="text-white">B</strong> — vanligt personbilskörkort, upp till 3 500 kg.</p>
              <p><strong className="text-white">B96</strong> — utökat B, 4 timmars kurs + prov, upp till 4 250 kg för bil+släp eller husbil.</p>
              <p><strong className="text-white">BE</strong> — släpvagnskörkort, upp till 7 000 kg.</p>
              <p><strong className="text-white">C1</strong> — medeltung lastbil, upp till 7 500 kg.</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/30">
                Se B-kortsvänliga husbilar <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-edge py-14">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl">Körkort & husbil</h2>
        <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card">
          {FAQ.map((f, i) => (
            <details key={i} className="group p-6">
              <summary className="cursor-pointer list-none font-display text-lg">{f.q}</summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
